import * as React from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ChartOptions } from 'chart.js';
import { Line } from 'react-chartjs-2';
import './style.scss';
import { calculatePercentage } from '../../utils/utils';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface ChartProps {
    symbol: string;
    value: number;
    labels: Array<string>;
    prices: Array<number>;
    currentPeriod: string;
    updateChart?: (period: string) => void;
}

export const ChartComponent: React.FC<ChartProps> = ({ symbol, value, currentPeriod, labels, prices, updateChart }) => {
    // Determine color based on price performance
    const isPositive = prices[0] <= value;
    const lineColor = isPositive ? 'rgb(0, 200, 20)' : 'rgb(255, 100, 0)';
    const fillColor = isPositive ? 'rgba(0, 200, 20, 0.1)' : 'rgba(255, 100, 0, 0.1)';
    
    // Chart data with improved styling
    const data = {
        labels,
        datasets: [
            {
                label: symbol,
                data: prices,
                borderColor: lineColor,
                backgroundColor: fillColor,
                borderWidth: 2.5,
                fill: true,
                tension: 0.4,
                pointRadius: 0,
                pointHoverRadius: 6,
                pointBackgroundColor: lineColor,
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                pointHoverBorderWidth: 3,
            },
        ],
    };

    const options: ChartOptions<'line'> = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            intersect: false,
            mode: 'index' as const,
        },
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: false,
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                padding: 12,
                titleFont: { size: 13, weight: 'bold' },
                bodyFont: { size: 12 },
                borderColor: 'rgba(255, 255, 255, 0.2)',
                borderWidth: 1,
                cornerRadius: 6,
                displayColors: true,
                callbacks: {
                    title: (context) => {
                        return context[0].label || '';
                    },
                    label: (context) => {
                        return `${symbol}: $${parseFloat(context.parsed.y.toString()).toFixed(2)}`;
                    }
                }
            },
        },
        scales: {
            x: {
                display: false,
                grid: {
                    display: false,
                }
            },
            y: {
                display: false,
                grid: {
                    display: false,
                }
            },
        },
    };
    
    const change = (value - prices[0]).toFixed(2);
    const changePercent = calculatePercentage(value, prices[0]);
    const isToday = currentPeriod === '1D';
    
    return <div className='chartWrapper'>
        <div className='header'>
            <div className='title'>{symbol}</div>
            <div className='equity'>${value.toFixed(2)}</div>
            <div className={`change ${changePercent > 0 ? 'green' : 'red'}`}>
                {changePercent > 0 ? <i className='bx bx-caret-up green'></i> : <i className='bx bx-caret-down red'></i>}
                {change} ({changePercent}%) 
                { isToday ? (<span>Today</span>) : null }
            </div>
        </div>
        <div className='chart'>
            <Line data={data} options={options} />
        </div>
        { updateChart ? (<ul className='chartOptions'>
            <li className={currentPeriod === '1D' ? 'active' : ''} ><a onClick={() => updateChart('1D')}>1D</a></li>
            <li className={currentPeriod === '1W' ? 'active' : ''} ><a onClick={() => updateChart('1W')}>1W</a></li>
            <li className={currentPeriod === '1M' ? 'active' : ''} ><a onClick={() => updateChart('1M')}>1M</a></li>
            <li className={currentPeriod === '3M' ? 'active' : ''} ><a onClick={() => updateChart('3M')}>3M</a></li>
            <li className={currentPeriod === 'YTD' ? 'active' : ''} ><a onClick={() => updateChart('YTD')}>YTD</a></li>
            <li className={currentPeriod === '6M' ? 'active' : ''} ><a onClick={() => updateChart('6M')}>6M</a></li>
            <li className={currentPeriod === '1A' ? 'active' : ''} ><a onClick={() => updateChart('1A')}>1Y</a></li>
        </ul>) : null }
    </div>;
}